import { WorkspaceTabsAction } from './actions';
import { MAX_WORKSPACE_TABS, WorkspaceTabsActionTypes, WorkspaceTabsState } from './types';

const WORKSPACE_TABS_SESSION_KEY = 'flora_workspace_tabs';

const EMPTY_STATE: WorkspaceTabsState = {
  tabs: [],
  activeKey: null,
};

const loadState = (): WorkspaceTabsState => {
  try {
    const raw = sessionStorage.getItem(WORKSPACE_TABS_SESSION_KEY);
    if (!raw) {
      return EMPTY_STATE;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tabs)) {
      return EMPTY_STATE;
    }
    return {
      tabs: parsed.tabs.slice(0, MAX_WORKSPACE_TABS),
      activeKey: parsed.activeKey ?? null,
    };
  } catch {
    return EMPTY_STATE;
  }
};

const saveState = (state: WorkspaceTabsState) => {
  sessionStorage.setItem(WORKSPACE_TABS_SESSION_KEY, JSON.stringify(state));
};

const WorkspaceTabs = (
  state: WorkspaceTabsState = loadState(),
  action: WorkspaceTabsAction
): WorkspaceTabsState => {
  switch (action.type) {
    case WorkspaceTabsActionTypes.OPEN_TAB: {
      const existing = state.tabs.find((tab) => tab.key === action.payload.key);
      if (existing) {
        const next = {
          ...state,
          activeKey: action.payload.key,
          tabs: state.tabs.map((tab) =>
            tab.key === action.payload.key
              ? {
                  ...tab,
                  title: action.payload.title || tab.title,
                  pathname: action.payload.pathname,
                  search: action.payload.search,
                }
              : tab
          ),
        };
        saveState(next);
        return next;
      }
      if (state.tabs.length >= MAX_WORKSPACE_TABS) {
        return state;
      }
      const next = {
        ...state,
        tabs: [...state.tabs, action.payload],
        activeKey: action.payload.key,
      };
      saveState(next);
      return next;
    }
    case WorkspaceTabsActionTypes.ACTIVATE_TAB: {
      const next = {
        ...state,
        activeKey: action.payload,
      };
      saveState(next);
      return next;
    }
    case WorkspaceTabsActionTypes.CLOSE_TAB: {
      const index = state.tabs.findIndex((tab) => tab.key === action.payload);
      if (index === -1) {
        return state;
      }
      const tabs = state.tabs.filter((tab) => tab.key !== action.payload);
      let activeKey = state.activeKey;
      if (state.activeKey === action.payload) {
        const nextTab = tabs[index] || tabs[index - 1] || null;
        activeKey = nextTab ? nextTab.key : null;
      }
      const next = { tabs, activeKey };
      saveState(next);
      return next;
    }
    case WorkspaceTabsActionTypes.UPDATE_TAB_TITLE: {
      const next = {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.key === action.payload.key ? { ...tab, title: action.payload.title } : tab
        ),
      };
      saveState(next);
      return next;
    }
    case WorkspaceTabsActionTypes.UPDATE_TAB_UI_STATE: {
      const next = {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.key === action.payload.key
            ? {
                ...tab,
                uiState: {
                  ...(tab.uiState || {}),
                  ...action.payload.uiState,
                },
              }
            : tab
        ),
      };
      saveState(next);
      return next;
    }
    case WorkspaceTabsActionTypes.CLEAR_TABS:
      sessionStorage.removeItem(WORKSPACE_TABS_SESSION_KEY);
      return EMPTY_STATE;
    default:
      return state;
  }
};

export default WorkspaceTabs;
